import mapDetailLow from '@/assets/images/map-detail-low.png'
import mapDetailMedium from '@/assets/images/map-detail-medium.png'
import mapDetailHigh from '@/assets/images/map-detail-high.png'
import mapDetailHighest from '@/assets/images/map-detail-highest.png'
import mapDetailTopo from '@/assets/images/map-detail-topo.png'
import type { Vertical } from '@/types/verticals'

type MapDetail = {
  [x: string]: {
    id: string
    label: string
    modalLabel: string
    img: unknown
    description: Record<Vertical, string>
    shortDescription: Record<Vertical, string>
    zoom: string
    visibility: string
  }
}

export const DOWNLOAD_EXPLANATION: Record<Vertical, string> = {
  hunt: 'include all layers, basemaps (including Topo), roads, and trails.',
  offroad:
    'include your basemaps (including Topo) and content from modes: roads, trails, and recreation points.',
  backcountry:
    'include your basemaps (including Topo) and content from modes: roads, trails, and recreation points.',
  fish: 'include all layers, basemaps (including Topo), roads, and trails.',
}

export const MAP_DETAIL_CONSTANTS_TOPO: MapDetail = {
  topo: {
    id: 'topo',
    label: 'Topo',
    modalLabel: 'Topo (only)',
    img: mapDetailTopo,
    description: {
      hunt: 'All layers, roads, trails, and contour lines in topographical format (no Satellite Imagery).',
      offroad:
        'All roads, trails and contour lines in topographical format (no Satellite Imagery).',
      backcountry:
        'All roads, trails and contour lines in topographical format (no Satellite Imagery).',
      fish: 'Topographical basemap only but all map data.',
    },
    shortDescription: {
      hunt: 'Best for large areas with topo lines and no satellite imagery',
      offroad: 'Best for large areas with topo lines and no satellite imagery',
      backcountry:
        'Best for large areas with topo lines and no satellite imagery',
      fish: 'Topographical basemap only but all map data.',
    },
    zoom: 'N/A',
    visibility: 'Elevation contour lines',
  },
}

export const MAP_DETAIL_CONSTANTS_HIGHEST: MapDetail = {
  highest: {
    id: 'highest',
    label: 'Highest',
    modalLabel: 'Highest',
    img: mapDetailHighest,
    description: {
      hunt: 'Highest resolution Satellite imagery and Topo, with all layers. Very large download sizes per square mile.  Best for saving specific properties.',
      offroad:
        'Highest resolution Satellite imagery and Topo.  Very large download sizes per square mile. Good for saving shorter trails or areas around your campsite.',
      backcountry:
        'Highest resolution Satellite imagery and Topo.  Very large download sizes per square mile. Good for saving shorter trails or areas around your campsite.',
      fish: 'Largest download size. Includes all basemaps and map data. Contains the most detail available.',
    },
    shortDescription: {
      hunt: 'Best for saving specific properties with full satellite detail',
      offroad: 'Best for saving specific areas around a campsite',
      backcountry: 'Best for saving specific areas around a campsite',
      fish: 'Largest download size. Includes all basemaps and map data. Contains the most detail available.',
    },
    zoom: '19',
    visibility: 'Individual trees, single track trails',
  },
}

export const MAP_DETAIL_CONSTANTS: MapDetail = {
  low: {
    id: 'low',
    label: 'Low',
    modalLabel: 'Low',
    img: mapDetailLow,
    description: {
      hunt: 'Low resolution Satellite imagery and Topo, with all layers.  Small download size that is best for saving large areas.',
      offroad:
        'Low resolution Satellite imagery and Topo.  Small download size that is best for saving large areas.',
      backcountry:
        'Low resolution Satellite imagery and Topo.  Small download size that is best for saving large areas.',
      fish: 'Smallest download size that contains all basemaps and map data.',
    },
    shortDescription: {
      hunt: 'Best for saving large areas',
      offroad: 'Best for saving large areas',
      backcountry: 'Best for saving large areas',
      fish: 'Smallest download size that contains all basemaps and map data.',
    },
    zoom: '13',
    visibility: 'Small town, forest section',
  },
  medium: {
    id: 'medium',
    label: 'Medium',
    modalLabel: 'Medium',
    img: mapDetailMedium,
    description: {
      hunt: 'Medium resolution Satellite imagery and Topo, with all layers.  Great for saving an entire hunting unit or county in one map.',
      offroad:
        'Medium resolution Satellite imagery and Topo.  Great for saving an entire trail network or county in one map.',
      backcountry:
        'Medium resolution Satellite imagery and Topo.  Great for saving an entire trail network or county in one map.',
      fish: 'More detail than low but lacks some fine detail found in high. Includes all basemaps and map data.',
    },
    shortDescription: {
      hunt: 'Best for an entire hunting unit or county in one map',
      offroad: 'Best for an entire trail network or county in one map',
      backcountry: 'Best for an entire trail network or county in one map',
      fish: 'More detail than low but lacks some fine detail found in high. Includes all basemaps and map data.',
    },
    zoom: '16',
    visibility: '2 lane roads',
  },
  high: {
    id: 'high',
    label: 'High',
    modalLabel: 'High',
    img: mapDetailHigh,
    description: {
      hunt: 'High resolution Satellite imagery and Topo, with all layers.  Great for saving very specific areas in a hunting unit or area of interest.',
      offroad:
        'High resolution Satellite imagery and Topo.  Great for saving very specific areas in a trail network or area of interest.',
      backcountry:
        'High resolution Satellite imagery and Topo.  Great for saving very specific areas in a trail network or area of interest.',
      fish: 'Large download size. Includes all basemaps and map data. Shows fine details on the ground.',
    },
    shortDescription: {
      hunt: 'Best for very specific areas in a hunting unit or area of interest',
      offroad:
        'Best for very specific areas in a trail network or area of interest',
      backcountry:
        'Best for very specific areas in a trail network or area of interest',
      fish: 'Large download size. Includes all basemaps and map data. Shows fine details on the ground.',
    },
    zoom: '18',
    visibility: 'Houses, tree clusters',
  },
}

export const DEFAULT_NAME_REGEX: RegExp =
  /^Map \d{2}\/\d{2}\/\d{2} \d{2}:\d{2}$/
